import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { Row, Button } from 'antd'
import * as d3 from 'd3'
import moment from 'moment'
import { isValidExerciseRecord } from './tracpatch.error.filter'
import '../../../css/patient.scss'
import { CHART_COLORS } from '../../../util/chartPalette'
import { DataViewToggle, ChartDataGrid } from '../../graph/chartDataView'
import { styleAxisTicks, AXIS_TITLE_FONT_SIZE, AXIS_TITLE_FONT_WEIGHT } from './tracpatch.chart.style'

const TracPatchExerciseChart = (props) => {
  const svgRef = useRef(null)
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)
  const [days, setDays] = useState(30)
  const [view, setView] = useState('chart')

  useEffect(() => {
    if (view === 'chart') draw()
  }, [props.data, days, view])

  const getSessionsInWindow = () => {
    if (!props.data || props.data.length === 0) {
      return { sessions: [], windowStart: null, windowEnd: null }
    }
    const windowEnd = moment().startOf('day')
    const windowStart = moment().subtract(days - 1, 'days').startOf('day')
    const filterEnd = moment().endOf('day')

    // Collapse to one record per day: max peakFlexion + max peakExtension
    // across all sets that day. Tooltip shows session count + cumulative reps.
    // Sets failing the centralized error filter (flexion > 135°,
    // extension < -10°, range < 5°) are dropped before aggregation —
    // they're sensor / detection junk, not real performance.
    const byDay = {}
    props.data.forEach((d) => {
      if (!d.completedAt) return
      const m = moment(d.completedAt)
      if (!m.isSameOrAfter(windowStart) || !m.isSameOrBefore(filterEnd)) return
      if (!isValidExerciseRecord(d)) return
      const key = m.format('YYYY-MM-DD')
      if (!byDay[key]) {
        byDay[key] = {
          date: m.clone().startOf('day').toDate(),
          // Actual timestamp of the day's last completed set, for the grid.
          time: d.completedAt,
          peakFlexion: d.peakFlexion,
          peakExtension: d.peakExtension,
          sessionsCount: 1,
          repsCompleted: d.repsCompleted || 0,
          requiredReps: d.requiredReps || 0,
        }
      } else {
        const bucket = byDay[key]
        if (d.peakFlexion > bucket.peakFlexion) bucket.peakFlexion = d.peakFlexion
        if (d.peakExtension > bucket.peakExtension) bucket.peakExtension = d.peakExtension
        if (m.isAfter(moment(bucket.time))) bucket.time = d.completedAt
        bucket.sessionsCount += 1
        bucket.repsCompleted += d.repsCompleted || 0
        bucket.requiredReps += d.requiredReps || 0
      }
    })

    const sessions = Object.keys(byDay).sort().map((k) => byDay[k])
    return { sessions, windowStart: windowStart.toDate(), windowEnd: windowEnd.toDate() }
  }

  const draw = () => {
    const { sessions, windowStart, windowEnd } = getSessionsInWindow()
    if (!sessions || sessions.length === 0) return

    const margin = { top: 32, right: 20, bottom: 60, left: 50 }
    const screenWidth = containerRef.current ? containerRef.current.offsetWidth : 400
    const screenHeight = 300
    const width = screenWidth - margin.left - margin.right
    const height = screenHeight - margin.top - margin.bottom

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const g = svg
      .attr('width', screenWidth)
      .attr('height', screenHeight)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    // Clamp the x-axis right edge to the last data point so trailing
    // days with no data don't render as empty space.
    const lastSessionDate = sessions[sessions.length - 1].date
    const effectiveEnd = lastSessionDate < windowEnd ? lastSessionDate : windowEnd
    const effectiveDays = Math.max(1, moment(effectiveEnd).diff(moment(windowStart), 'days') + 1)
    const x = d3.scaleTime().domain([windowStart, effectiveEnd]).range([0, width])
    // y-axis bounds are always derived from the last 7 days so 30/90 day
    // views match the 7-day view's scale.
    const sevenDayStart = moment().subtract(6, 'days').startOf('day')
    const sevenDayEnd = moment().endOf('day')
    const sevenDayData = (props.data || []).filter((d) => {
      if (!d.completedAt) return false
      const t = moment(d.completedAt)
      return t.isSameOrAfter(sevenDayStart) && t.isSameOrBefore(sevenDayEnd)
    })
    const maxFlexion = d3.max(sevenDayData, (d) => d.peakFlexion)
    const minExtension = d3.min(sevenDayData, (d) => d.peakExtension)
    const yMax = Math.max(130, (maxFlexion != null ? maxFlexion : 0) + 10)
    const yMin = Math.min(-15, (minExtension != null ? minExtension : 0) - 5)
    const y = d3.scaleLinear().domain([yMin, yMax]).range([height, 0])

    const tickCount = Math.min(days > 20 ? 10 : 7, effectiveDays)
    const tickValues = []
    for (let i = 0; i < tickCount; i++) {
      const dayOffset = tickCount === 1 ? 0 : Math.round((i / (tickCount - 1)) * (effectiveDays - 1))
      tickValues.push(moment(windowStart).add(dayOffset, 'days').toDate())
    }
    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x).tickValues(tickValues).tickFormat(d3.timeFormat('%m/%d')))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)')
      .call(styleAxisTicks, days > 20 ? 10 : 11)
      .style('fill', 'black')
      .style('stroke', 'none')

    g.append('text')
      .attr('text-anchor', 'end')
      .attr('x', width / 2 + margin.left)
      .attr('y', height + margin.bottom - 5)
      .attr('stroke', 'none')
      .attr('fill', 'black')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text('Date')

    g.append('g')
      .call(d3.axisLeft(y).ticks(6))
      .selectAll('text')
      .style('fill', 'black')
      .style('stroke', 'none')
      .call(styleAxisTicks, 10)

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -margin.left + 12)
      .attr('x', -height / 2)
      .attr('text-anchor', 'middle')
      .attr('fill', 'black')
      .attr('stroke', 'none')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text('Degrees (°)')

    const surgeryRaw = props.patient?.surgery?.date
    if (surgeryRaw) {
      const surgeryDate = moment(surgeryRaw).startOf('day').toDate()
      if (surgeryDate >= windowStart && surgeryDate <= effectiveEnd) {
        const surgeryX = x(surgeryDate)
        g.append('line')
          .attr('x1', surgeryX)
          .attr('x2', surgeryX)
          .attr('y1', 0)
          .attr('y2', height)
          .attr('stroke', '#595959')
          .attr('stroke-width', 1.5)
          .attr('stroke-dasharray', '4,3')
        const labelText = `Surgery ${moment(surgeryDate).format('MM/DD')}`
        const labelOnLeft = surgeryX > width - 80
        g.append('text')
          .attr('x', surgeryX + (labelOnLeft ? -4 : 4))
          .attr('y', 10)
          .attr('text-anchor', labelOnLeft ? 'end' : 'start')
          .attr('fill', '#595959')
          .attr('stroke', 'none')
          .style('font-size', '11px')
          .style('font-weight', '600')
          .text(labelText)
      }
    }

    const tooltip = d3.select(tooltipRef.current)

    const drawDots = (key, label, color) => {
      g.selectAll(`.dot-${key}`)
        .data(sessions.filter((d) => d[key] != null))
        .enter()
        .append('circle')
        .attr('class', `dot-${key}`)
        .attr('cx', (d) => x(d.date))
        .attr('cy', (d) => y(d[key]))
        .attr('r', 4)
        .attr('fill', color)
        .style('cursor', 'pointer')
        .on('mouseover', function (d) {
          const reps = d.requiredReps > 0
            ? `${d.repsCompleted}/${d.requiredReps}`
            : (d.repsCompleted || '—')
          const sets = `${d.sessionsCount} set${d.sessionsCount === 1 ? '' : 's'}`
          tooltip
            .style('visibility', 'visible')
            .html(
              `<b>Date</b>: ${moment(d.date).format('MM/DD')}` +
              `<br/><b>${label}</b>: ${Math.round(d[key])}°` +
              `<br/><b>Sets</b>: ${sets}` +
              `<br/><b>Reps</b>: ${reps}`
            )
            .style('top', d3.select(this).attr('cy') - 50 + 'px')
            .style('left', parseInt(d3.select(this).attr('cx')) + margin.left + 10 + 'px')
        })
        .on('mouseout', () => tooltip.style('visibility', 'hidden'))
    }

    const flexLine = d3
      .line()
      .defined((d) => d.peakFlexion != null)
      .x((d) => x(d.date))
      .y((d) => y(d.peakFlexion))
      .curve(d3.curveMonotoneX)
    g.append('path')
      .datum(sessions)
      .attr('fill', 'none')
      .attr('stroke', CHART_COLORS.lineBlue)
      .attr('stroke-width', 2)
      .attr('d', flexLine)

    drawDots('peakFlexion', 'Peak Flexion', CHART_COLORS.lineBlue)

    const extLine = d3
      .line()
      .defined((d) => d.peakExtension != null)
      .x((d) => x(d.date))
      .y((d) => y(d.peakExtension))
      .curve(d3.curveMonotoneX)
    g.append('path')
      .datum(sessions)
      .attr('fill', 'none')
      .attr('stroke', CHART_COLORS.secondaryRed)
      .attr('stroke-width', 2)
      .attr('d', extLine)

    drawDots('peakExtension', 'Peak Extension', CHART_COLORS.secondaryRed)

    // Inline value labels on every plotted day for both lines. Throttle on
    // longer windows so labels don't overlap; the latest day always renders.
    // Flexion above the dot, extension below — keeps both readable when the
    // two lines come close together.
    const drawValueLabels = (key, color, offsetY) => {
      const labelData = sessions.filter((d) => d[key] != null)
      const labelStep = labelData.length > 14 ? 2 : 1
      labelData.forEach((d, i) => {
        const fromEnd = labelData.length - 1 - i
        if (fromEnd % labelStep !== 0) return
        g.append('text')
          .attr('x', x(d.date))
          .attr('y', y(d[key]) + offsetY)
          .attr('text-anchor', 'middle')
          .attr('fill', color)
          .style('font-size', '10px')
          .style('font-weight', '600')
          .style('pointer-events', 'none')
          .text(Math.round(d[key]) + '°')
      })
    }
    drawValueLabels('peakFlexion', CHART_COLORS.lineBlue, -5)
    drawValueLabels('peakExtension', CHART_COLORS.secondaryRed, 12)

    const legend = [
      { label: 'Peak Flexion', color: CHART_COLORS.lineBlue },
      { label: 'Peak Extension', color: CHART_COLORS.secondaryRed },
    ]
    let xOffset = 0
    legend.forEach((item) => {
      g.append('rect').attr('x', xOffset).attr('y', -22).attr('width', 10).attr('height', 10).attr('fill', item.color)
      g.append('text').attr('x', xOffset + 14).attr('y', -13).attr('fill', 'black').attr('stroke', 'none').style('font-size', '12px').text(item.label)
      xOffset += 110
    })
  }

  const { sessions } = getSessionsInWindow()
  const gridRows = sessions.map((d) => ({
    date: d.time || d.date,
    peakFlexion: d.peakFlexion != null ? Math.round(d.peakFlexion) + '°' : '—',
    peakExtension: d.peakExtension != null ? Math.round(d.peakExtension) + '°' : '—',
  }))
  const gridColumns = [
    { title: 'Peak Flexion', dataIndex: 'peakFlexion', key: 'peakFlexion' },
    { title: 'Peak Extension', dataIndex: 'peakExtension', key: 'peakExtension' },
  ]

  return (
    <>
      <center>
        <h4>{props.exerciseName || 'TracPatch Exercise'}</h4>
      </center>
      <Row justify='start' className='chartControls' style={{ marginBottom: 8, paddingLeft: 12 }}>
        <Button.Group size='small'>
          <Button type={days === 7 ? 'primary' : 'default'} onClick={() => setDays(7)}>7 Days</Button>
          <Button type={days === 30 ? 'primary' : 'default'} onClick={() => setDays(30)}>30 Days</Button>
          <Button type={days === 90 ? 'primary' : 'default'} onClick={() => setDays(90)}>90 Days</Button>
        </Button.Group>
        <DataViewToggle
          active={view === 'data'}
          onClick={() => setView(view === 'data' ? 'chart' : 'data')}
        />
      </Row>
      {sessions.length > 0 ? (
        view === 'data' ? (
          <ChartDataGrid rows={gridRows} valueColumns={gridColumns} csvName='tracpatch-exercise-rom' />
        ) : (
          <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
            <div ref={tooltipRef} className='chartToolTip' style={{ position: 'absolute', visibility: 'hidden', zIndex: 10 }} />
            <svg style={{ width: '100%', height: '300px' }} ref={svgRef} />
          </div>
        )
      ) : (
        <Row justify='center' align='middle' style={{ height: 200 }}>
          <h2>Data not available</h2>
        </Row>
      )}
    </>
  )
}

const mapStateToProps = (state) => ({ patient: state.patient.patient })
const mapDispatchToProps = () => ({})
export default connect(mapStateToProps, mapDispatchToProps)(TracPatchExerciseChart)
